import * as React from "react";
import { FunctionComponent, useState } from "react";
import { red } from "@mui/material/colors";

//Material UI
import { createTheme, ThemeProvider as MuiThemeProvider, ThemeOptions } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import CssBaseline from "@mui/material/CssBaseline";

//Custom components
import ThemeContext from "@/components/ThemeContext/ThemeContext";

//Types
import { IThemeProvider, Theme } from "@/@types/context/ThemeContext";

// it could be your App.tsx file or theme file that is included in your tsconfig.json
import { Theme as MuiTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true; // removes the `xs` breakpoint
    sm: true;
    custom: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    h7: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    h7?: React.CSSProperties;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    mint: Palette['primary'];
  }

  interface PaletteOptions {
    mint?: PaletteOptions['primary'];
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h7: true;
  }
}
// declare module "@mui/material/styles" {
//   interface TypographyVariants {
//     titleText: React.CSSProperties;
//     normalText: React.CSSProperties;
//     smallText: React.CSSProperties;
//   }

//   // allow configuration using `createTheme`
//   interface TypographyVariantsOptions {
//     titleText?: React.CSSProperties;
//     normalText?: React.CSSProperties;
//     smallText?: React.CSSProperties;
//   }
// }

// // Update the Typography's variant prop options
// declare module "@mui/material/Typography" {
//   interface TypographyPropsVariantOverrides {
//     titleText: true;
//     normalText: true;
//     smallText: true;
//   }
// }

export const appHeaderTheme = createTheme({
  palette: {
    secondary: {
      main: grey[800]
    }
  }
})

export const hgssTheme = createTheme({
  palette: {
    primary: {
      main: "#FF0000",
      dark: "#b20000",
      light: "#ff4c4c"
    },
    secondary: {
      main: "#005a96",
      dark: "#044a79",
      light: "#83b5d6",
    }
  }
})

export const hpsTheme = createTheme({
  palette: {
    primary: {
      main: "#005a96",
      dark: "#044a79",
      light: "#83b5d6",
    },
    secondary: {
      main: "#FF0000",
      dark: "#b20000",
      light: "#ff4c4c"
    }
  }
})

export const regUserTheme = createTheme({
  palette: {
    primary: {
      main: "#002c4a",
      dark: "#001d31",
      light: "#003b64"
    },
    secondary: {
      main: "#FF0000",
      dark: "#b20000",
      light: "#ff4c4c"
    }
  }
})

const ThemeProvider: FunctionComponent<IThemeProvider> = (props) => {
  const THEME_LIGHT = "light";
  const THEME_DARK = "dark";

  const [theme, setTheme] = useState<Theme>(THEME_LIGHT);
  const [dyslexic, setDyslexic] = useState<boolean>(false);

  const switchTheme = (t: Theme): void => {
    setTheme(t);
  };

  const lightTheme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#FF0000",
        dark: "#b20000",
        light: "#ff4c4c"
      },
      secondary: {
        main: "#005a96",
        dark: "#044a79",
        light: "#83b5d6",
      },
      background: {
        default: "#fafafa",
        paper: "#fafafa"
      }
    },
    /*typography: dyslexic ? {
      fontFamily: [
        'open-dyslexic',
        'Roboto',
        'Arial',
        'sans-serif'
      ].join(',')} : undefined*/
    typography: {
      fontFamily: dyslexic ? [
        'open-dyslexic',
        'Roboto',
        'Arial',
        'sans-serif'
      ].join(',') : undefined
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&:-webkit-autofill": {
              transitionDelay: "9999s",
              transitionProperty: "background-color, color"
            }
          }
        }
      }
    },

  });

  const lightThemeWithOverrides = createTheme({
    ...lightTheme,
    components: {
      ...lightTheme.components,
      MuiAlert: {
        styleOverrides: {
          filledInfo: {
            borderWidth: "3px",
            borderStyle: "solid",
            borderColor: lightTheme.palette.primary.main,
            backgroundColor: lightTheme.palette.background.paper,
            color: lightTheme.palette.text.primary
          }
        }
      }
    }
  })

  const darkTheme = createTheme({
    palette: {
      mode: "dark",
      primary: lightTheme.palette.primary,
      secondary: lightTheme.palette.secondary,
      background: {
        default: "#424242",
        paper: "#424242"
      },
    },
    components: lightTheme.components
  });

  const darkThemeWithOverrides = createTheme({
    ...darkTheme,
    components: {
      ...darkTheme.components,
      MuiAlert: {
        styleOverrides: {
          filledInfo: {
            borderWidth: "3px",
            borderStyle: "solid",
            borderColor: lightTheme.palette.primary.main,
            backgroundColor: darkTheme.palette.background.paper,
            color: darkTheme.palette.text.primary
          }
        }
      }
    }
  })


  const publicTheme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#005a96",
        dark: "#003b63",
        light: "#0079c9",
        // Text/icon color on primary-colored backgrounds (contained buttons, header, map controls)
        contrastText: "#DDFFD7"
      },
      secondary: {
        main: "#FF0000",
        dark: "#b20000",
        light: "#ff4c4c",
      },
      // Accent color used for text/icons sitting on primary-colored backgrounds (header, map controls)
      mint: {
        main: "#DDFFD7",
      },
      background: {
        default: "#fafafa",
        paper: "#fafafa"
      }
    }, typography: {
      fontFamily: dyslexic ? [
        'open-dyslexic',
        'Roboto',
        'Arial',
        'sans-serif'
      ].join(',') : undefined,
      h6: {
        fontWeight: 400, 
        fontSize: "1.4rem", 
        lineHeight: 1.35,
        color: "#005a96"
      },
      h7:  {
        fontWeight: 400, 
        fontSize: "1.2rem", 
        lineHeight: 1.35,
        color: "#005a96"
      },

    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        custom: 750,
        md: 900,
        lg: 1200,
        xl: 1536,
        xxl: 2320
      },
    },
  });




  const getMuiTheme = () => {
    if (theme === THEME_LIGHT) {
      return lightThemeWithOverrides;
    } else if (theme === THEME_DARK) {
      return darkThemeWithOverrides;
    } else {
      return lightThemeWithOverrides;
    }
  };

  const toggleDyslexicFont = () => {
    setDyslexic(prevValue => !prevValue);
  }

  const resetDyslexicFont = () => {
    setDyslexic(false);
  }

  const muiTheme = getMuiTheme();

  const providerValue = {
    theme,
    setTheme: switchTheme,
    dyslexic,
    toggleDyslexicFont,
    resetDyslexicFont,
    publicTheme
  };

  return (
    <ThemeContext.Provider value={providerValue}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {props.children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;

